import React, { SetStateAction, Dispatch } from 'react';
import { Typography } from '@/components/common/typography/typography';
import { Button, Icon, ModalUploadImage } from '@/components/common';
import { FieldValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { ContainerImage } from './ContainerImage';
import Icons from '@/const/icons';
import Image from 'next/image';
import clsx from 'clsx';
import { getImageSizeByChannel } from '@/lib';
import { CampaignChannels } from '@/interfaces';
import useModal from '@/hooks/useModal';
import { ImageEditor } from '@/components/dashboard';

interface Props {
	file: File | null;
	srcImg: string | null;
	setSrcImg: Dispatch<SetStateAction<string | null>>;
	setFile: Dispatch<SetStateAction<File | null>>;
	setSourceType: Dispatch<SetStateAction<string>>;
	register: UseFormRegister<FieldValues>;
	isCaseReadOnly: boolean;
	setValue: UseFormSetValue<FieldValues>;
	sourceType: string;
	handleSetFile: (url: string | null, file: File | null) => void;
}

export const UploadImage: React.FC<Props> = ({
	file,
	srcImg,
	setFile,
	register,
	setValue,
	setSrcImg,
	sourceType,
	handleSetFile,
	setSourceType,
	isCaseReadOnly,
}) => {
	const {
		Modal: ModalImageEditor,
		show: showModalImageEditor,
		hide: hideModalImageEditor,
	} = useModal();

	const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
	const [imageToEdited, setImageToEdited] = React.useState('');

	const handleDeleteImage = () => {
		setSrcImg(null);
		setFile(null);
	};
	const handleImageEdited = (image: string) => {
		setFile(null);
		setSrcImg(image);
		setSourceType('file');
		setValue('cover_image_url', image, { shouldDirty: true });
	};
	const isFreePickImg = sourceType === 'url';
	return (
		<ContainerImage
			title="Upload Image"
			tooltip={
				<div>
					<Typography type="bold-12">Upload Image</Typography>
					<Typography type="regular-12">
						The image must be in JPG or PNG format, with dimensions no smaller
						than 900x345 pixels and a maximum file size of 25 MB.
					</Typography>
				</div>
			}
		>
			{!srcImg || isFreePickImg ? (
				<Button
					disabled={isCaseReadOnly}
					decoration="secondary"
					label="Upload Image"
					iconLeft
					icon={Icons.documentUpload}
					size="full"
					className={clsx(' !py-1 h-12')}
					classNameButton="!size-4"
					onClick={() => {
						setIsModalOpen(!isModalOpen);
					}}
				/>
			) : (
				<div className="h-12" />
			)}
			{srcImg && !isFreePickImg ? (
				<div className="flex items-center justify-between mt-4 w-full ">
					<div className="relative">
						<Image
							src={srcImg}
							alt="case image"
							width={269}
							height={300}
							className="w-[269px] h-[137px] object-contain"
							quality={75}
						/>
						<div
							className={clsx(
								'absolute inset-0 bg-black opacity-0 transition-opacity hover:opacity-60'
							)}
						>
							<button
								type="button"
								className="top-[35%] right-[40%] absolute p-2 rounded-lg"
								onClick={() => {
									setImageToEdited(srcImg);
									showModalImageEditor();
								}}
							>
								<Icon src={Icons.galleryEdit} className="!size-6 text-white" />
							</button>
						</div>
					</div>
					<button
						disabled={isCaseReadOnly}
						type="button"
						className="hover:opacity-60"
						onClick={handleDeleteImage}
					>
						<Icon
							src={Icons.trash}
							className="text-main-red"
							width={16}
							height={16}
						/>
					</button>
				</div>
			) : (
				<Image
					alt="no image"
					src={Icons.imageEmptyState}
					width={125}
					height={65}
					className={clsx('!my-12 mx-auto')}
					unoptimized
				/>
			)}

			<ModalUploadImage
				file={isFreePickImg ? null : file}
				setFile={setFile}
				prevFile={isFreePickImg ? null : srcImg}
				setPrevFile={setSrcImg}
				register={register}
				isOpen={isModalOpen}
				setImage={handleSetFile}
				setIsModalOpen={setIsModalOpen}
			/>

			<ModalImageEditor
				onClose={() => {
					hideModalImageEditor();
					setImageToEdited('');
				}}
				classNameCardContainer={'xl:!w-[1329px] !h-[732px]  !p-6'}
				classNameContainerChildren="!px-0"
				noMaxHeight
			>
				<ImageEditor
					image={imageToEdited}
					handleImageEdited={handleImageEdited}
					aspectRatio={900 / 345}
					hasDownload={false}
					hideImageEditor={hideModalImageEditor}
					width={getImageSizeByChannel['FACEBOOK' as CampaignChannels]?.width}
					height={getImageSizeByChannel['FACEBOOK' as CampaignChannels]?.height}
				/>
			</ModalImageEditor>
		</ContainerImage>
	);
};
